/**
 * Shopify Cloud Service
 * Uses Firebase Cloud Functions to interact with Shopify API (CORS-safe)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';

export interface ShopifyProduct {
  id: number;
  title: string;
  body_html: string;
  vendor: string;
  product_type: string;
  tags: string;
  variants: Array<{
    id: number;
    title: string;
    price: string;
    sku: string;
    inventory_quantity: number;
  }>;
  images: Array<{
    id: number;
    src: string;
    alt: string | null;
  }>;
  handle: string;
  status?: string;
}

export interface ShopifyStore {
  name: string;
  domain: string;
  email: string;
  currency: string;
}

class ShopifyCloudService {
  /**
   * Test connection to Shopify
   */
  async testConnection(): Promise<{ success: boolean; storeName?: string; error?: string }> {
    try {
      const testConnection = httpsCallable(functions, 'shopifyTestConnection');
      const result = await testConnection();
      return result.data as { success: boolean; storeName?: string; error?: string };
    } catch (error) {
      console.error('Shopify connection test failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get all products from Shopify
   */
  async getAllProducts(): Promise<ShopifyProduct[]> {
    try {
      const getProducts = httpsCallable(functions, 'shopifyGetProducts');
      const result = await getProducts({ limit: 250 });
      const data = result.data as { success: boolean; products: ShopifyProduct[]; count: number };

      if (!data.success) {
        throw new Error('Failed to fetch products');
      }

      return data.products;
    } catch (error) {
      console.error('Error fetching Shopify products:', error);
      throw error;
    }
  }

  /**
   * Get store information
   */
  async getStore(): Promise<ShopifyStore> {
    try {
      const getStore = httpsCallable(functions, 'shopifyGetStore');
      const result = await getStore();
      const data = result.data as { success: boolean; store: ShopifyStore };

      if (!data.success) {
        throw new Error('Failed to fetch store info');
      }

      return data.store;
    } catch (error) {
      console.error('Error fetching Shopify store:', error);
      throw error;
    }
  }

  /**
   * Sync products to Firestore (server-side)
   */
  async syncProducts(): Promise<{ success: boolean; count: number; products: Array<{ id: number; title: string }> }> {
    try {
      const syncProducts = httpsCallable(functions, 'shopifySyncProducts');
      const result = await syncProducts();
      return result.data as { success: boolean; count: number; products: Array<{ id: number; title: string }> };
    } catch (error) {
      console.error('Error syncing Shopify products:', error);
      throw error;
    }
  }

  /**
   * Generate AI audit prompts based on products
   */
  generateAuditPrompts(products: ShopifyProduct[]): string[] {
    const prompts: string[] = [];
    const productTypes = [...new Set(products.map(p => p.product_type).filter(Boolean))];

    // Sample products for specific queries
    const sampleProducts = products.slice(0, 5);

    // Generic category prompts
    productTypes.forEach(type => {
      if (type) {
        prompts.push(`What are the best ${type.toLowerCase()} available online?`);
        prompts.push(`Where can I buy ${type.toLowerCase()}?`);
        prompts.push(`Compare different ${type.toLowerCase()} options`);
      }
    });

    // Specific product prompts
    sampleProducts.forEach(product => {
      prompts.push(`Tell me about ${product.title}`);
      prompts.push(`Where can I buy ${product.title}?`);
      if (product.vendor) {
        prompts.push(`What products does ${product.vendor} make?`);
      }
    });

    // Store-specific prompts
    prompts.push('What are the best insurance training courses?');
    prompts.push('Where can I get surety bond education?');
    prompts.push('What online courses are available for insurance professionals?');

    return prompts.slice(0, 30); // Limit to 30 prompts
  }
}

// Export singleton instance
export const shopifyCloudService = new ShopifyCloudService();
